import type { MethodologyNode } from '../../services/methodology/methodologyService';

export interface MethodologyTreeNode extends MethodologyNode {
  children: MethodologyTreeNode[];
}

const byOrder = (a: MethodologyNode, b: MethodologyNode) =>
  a.sort_order - b.sort_order || a.title.localeCompare(b.title, 'ru');

/**
 * Собирает дерево из плоского списка. Узел с несуществующим родителем
 * поднимается в корень — чтобы статья не пропала из сайдбара.
 */
export function buildMethodologyTree(nodes: MethodologyNode[]): MethodologyTreeNode[] {
  const byId = new Map<string, MethodologyTreeNode>();
  for (const node of nodes) byId.set(node.id, { ...node, children: [] });

  const roots: MethodologyTreeNode[] = [];
  for (const node of byId.values()) {
    const parent = node.parent_id ? byId.get(node.parent_id) : undefined;
    (parent ? parent.children : roots).push(node);
  }

  const sortDeep = (list: MethodologyTreeNode[]) => {
    list.sort(byOrder);
    list.forEach((n) => sortDeep(n.children));
  };
  sortDeep(roots);
  return roots;
}

/** Цепочка предков от корня до узла (включительно). Пусто, если узла нет. */
export function getMethodologyPath(nodes: MethodologyNode[], id: string): MethodologyNode[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const path: MethodologyNode[] = [];
  const seen = new Set<string>();
  let current = byId.get(id);
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    path.unshift(current);
    current = current.parent_id ? byId.get(current.parent_id) : undefined;
  }
  return path;
}

/**
 * Разделы верхнего уровня — вкладки в sub-header. Статьи в корне при просмотре
 * не показываются: на верхнем уровне допускаются только разделы.
 */
export function getMethodologySections(nodes: MethodologyNode[]): MethodologyNode[] {
  return nodes
    .filter((n) => n.parent_id === null && n.kind === 'section')
    .sort(byOrder);
}

/** Все потомки узла (без него самого) — содержимое сайдбара раздела. */
export function getMethodologyDescendants(nodes: MethodologyNode[], rootId: string): MethodologyNode[] {
  const childrenOf = new Map<string, MethodologyNode[]>();
  for (const node of nodes) {
    if (!node.parent_id) continue;
    const list = childrenOf.get(node.parent_id) ?? [];
    list.push(node);
    childrenOf.set(node.parent_id, list);
  }

  const result: MethodologyNode[] = [];
  const seen = new Set<string>([rootId]);
  const stack = [...(childrenOf.get(rootId) ?? [])];
  while (stack.length > 0) {
    const node = stack.pop()!;
    if (seen.has(node.id)) continue;
    seen.add(node.id);
    result.push(node);
    stack.push(...(childrenOf.get(node.id) ?? []));
  }
  return result;
}

/**
 * Оставляет узлы, чьё название содержит query, вместе с их предками.
 * expandedKeys — все предки совпадений, чтобы совпадения были видны.
 */
export function filterMethodologyTree(
  tree: MethodologyTreeNode[],
  query: string,
): { tree: MethodologyTreeNode[]; expandedKeys: string[] } {
  const needle = query.trim().toLocaleLowerCase('ru');
  if (!needle) return { tree, expandedKeys: [] };

  const expandedKeys: string[] = [];
  const walk = (list: MethodologyTreeNode[]): MethodologyTreeNode[] =>
    list.flatMap((node) => {
      const children = walk(node.children);
      if (children.length > 0) expandedKeys.push(node.id);
      if (children.length > 0 || node.title.toLocaleLowerCase('ru').includes(needle)) {
        return [{ ...node, children }];
      }
      return [];
    });

  return { tree: walk(tree), expandedKeys };
}
